import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { CardBlockScrollProvider } from '@shared/components/blocks/contexts/scroll';
import { MBButton } from '@shared/components/button';
import { MBIconV2 } from '@shared/components/image';
import { UnvalidatedStyles } from '@shared/components/text';
import { MBTextBody2, MBTextH4 } from '@shared/components/typography';
import { ExternalBatchContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext/BatchContext';
import {
  ResponsiveFormProvider,
  useResponsiveFormContext,
} from '@shared/scenes/entity/components/ResponsiveFormContext';
import { CardEntityCreateForm } from '@shared/scenes/entity/create/components/CardEntityCreateForm';
import { useCreateCardEntity } from '@shared/scenes/entity/create/hooks/useCreateCardEntity';
import { CREATE_MODAL_FIXED_WIDTH, Colors, EXTERNAL_FORM_MAX_WIDTH, grid } from '@shared/styles';
import { useChannel } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useUnsavedChangesNavigationWarning } from '@shared/util/navigation';

export function CreateCardEntityForm({
  channelId,
  templateSlug,
}: {
  channelId: number;
  templateSlug: string;
}) {
  const channel = useChannel({ channelId });
  const workflowTemplateId = channel?.workflowTemplateId;

  return (
    <CardBlockScrollProvider>
      <ExternalBatchContextProvider>
        <ResponsiveFormProvider>
          <Form workflowTemplateId={workflowTemplateId ?? undefined} templateSlug={templateSlug} />
        </ResponsiveFormProvider>
      </ExternalBatchContextProvider>
    </CardBlockScrollProvider>
  );
}

function Form({
  workflowTemplateId,
  templateSlug,
}: {
  workflowTemplateId?: string;
  templateSlug: string;
}) {
  const isResponsiveForms = useResponsiveFormContext();

  const queryFieldOverrides = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fields = params.get('fields');
    if (!fields) {
      return undefined;
    }
    try {
      return JSON.parse(fields);
    } catch (error) {
      return undefined;
    }
  }, []);

  const {
    components,
    fields,
    updateField,
    errors,
    isDirty,
    onPressSave,
    status,
    viewTemplateTitle,
    viewTemplate,
    resetForm,
  } = useCreateCardEntity({
    workflowTemplateId,
    viewTemplateId: templateSlug,
    queryFieldOverrides,
  });
  const formErrors = errors.filter(error => !error.componentId);
  const hasFormErrors = formErrors.length > 0;

  const isSaveSuccessful = status === 'success';
  const showUnsavedChangesWarning = !isSaveSuccessful && isDirty;
  useUnsavedChangesNavigationWarning(showUnsavedChangesWarning);

  if (isSaveSuccessful) {
    return <SaveSuccess resetForm={resetForm} />;
  }

  return (
    <MBView style={[styles.container, isResponsiveForms && styles.containerResponsive]}>
      <Header
        title={viewTemplateTitle}
        showSave={isDirty}
        onSave={onPressSave}
        showBorder={!hasFormErrors}
      />
      <MBView style={styles.form}>
        <CardEntityCreateForm
          components={components}
          fields={fields}
          updateField={updateField}
          errors={errors}
          workflowTemplateId={workflowTemplateId}
          viewTemplateId={viewTemplate?.id}
          isAuthenticated={false}
          style={[styles.formContent, isResponsiveForms && styles.formContentResponsive]}
        />
      </MBView>
    </MBView>
  );
}

function Header({
  title,
  showSave,
  onSave,
  showBorder = true,
}: {
  title: string;
  showSave: boolean;
  onSave: () => void;
  showBorder?: boolean;
}) {
  const isResponsiveForms = useResponsiveFormContext();
  return (
    <MBView
      style={[
        styles.header,
        showSave && styles.headerWithSave,
        isResponsiveForms && styles.headerResponsive,
      ]}
    >
      <MBView
        row
        centerVertically
        style={[
          styles.headerContent,
          !isResponsiveForms && showBorder && styles.headerBorder,
          isResponsiveForms && styles.headerContentResponsive,
        ]}
      >
        <MBTextH4
          align="left"
          ellipsizeMode="tail"
          numberOfLines={1}
          style={UnvalidatedStyles.numberOfLines(1)}
        >
          {title}
        </MBTextH4>
        {showSave && <MBButton title={i18n('common.create')} onPress={onSave} size="small" />}
      </MBView>
    </MBView>
  );
}

function SaveSuccess({ resetForm }: { resetForm: () => void }) {
  return (
    <MBView style={[styles.container, styles.successContainer]}>
      <MBView style={styles.successLogo}>
        <MBIconV2 name="coast-symbol" color={Colors.v2.green} size={grid(10)} />
      </MBView>
      <MBView style={styles.successText}>
        <MBTextH4 style={{ marginBottom: grid(1) }}>{i18n('externalForm.success.title')}</MBTextH4>
        <MBTextBody2>{i18n('externalForm.success.description')}</MBTextBody2>
        <MBButton
          title={i18n('common.createAnother')}
          onPress={resetForm}
          size="small"
          style={{ marginTop: grid(2) }}
        />
      </MBView>
    </MBView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '100vh',
  },
  containerResponsive: {
    backgroundColor: Colors.v2.backgroundGrayLight,
  },
  form: {
    flex: 1,
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  formContent: {
    width: '100%',
    maxWidth: EXTERNAL_FORM_MAX_WIDTH,
    alignSelf: 'center',
    paddingBottom: grid(8),
  },
  formContentResponsive: {
    maxWidth: CREATE_MODAL_FIXED_WIDTH,
  },
  header: {
    width: '100%',
    alignItems: 'center',
    paddingLeft: grid(1),
    paddingRight: grid(1),
  },
  headerResponsive: {
    backgroundColor: Colors.v2.white,
    borderBottomColor: Colors.v2.gray10,
    borderBottomWidth: 1,
  },
  headerWithSave: {
    justifyContent: 'space-between',
  },
  headerBorder: {
    borderBottomColor: Colors.v2.gray05,
    borderBottomWidth: 1,
  },
  headerContent: {
    width: '100%',
    maxWidth: EXTERNAL_FORM_MAX_WIDTH,
    height: 64,
    padding: grid(1),
    paddingHorizontal: grid(3),
    justifyContent: 'space-between',
  },
  headerContentResponsive: {
    maxWidth: CREATE_MODAL_FIXED_WIDTH,
    paddingLeft: grid(4),
  },
  successContainer: {
    backgroundColor: Colors.v2.green05,
    justifyContent: 'center',
  },
  successLogo: {
    paddingVertical: grid(2),
  },
  successText: {
    minWidth: 300,
    paddingVertical: grid(2),
    paddingHorizontal: grid(3),
    borderRadius: grid(1),
    backgroundColor: Colors.v2.white,
    borderColor: Colors.v2.green10,
    borderWidth: 1,
    textAlign: 'center',
    alignItems: 'center',
  },
});
